import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { FiFilter, FiX, FiChevronRight } from 'react-icons/fi';
import { FaInstagram, FaWhatsapp } from 'react-icons/fa6';
import ProductCard from '../components/ProductCard';
import PriceRangeSlider from '../components/PriceRangeSlider';
import SearchBar from '../components/SearchBar';
import { useProducts } from '../hooks/useProducts';
import { useBrands } from '../hooks/useBrands';
import { useCategories } from '../hooks/useCategories';
import { useLanguage } from '../context/LanguageContext';
import { slugify } from '../utils/slugify';
import { sortProductsByBoostAndPopularity, fetchSalesDataPublicSafe, fetchReviewsData } from '../utils/productSorting';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const BrandHeader = styled.div`
  background: ${props => props.$gradient || 'linear-gradient(135deg, #5efba9 0%, #34D399 100%)'};
  border-radius: 16px;
  padding: 48px 32px;
  margin-bottom: 32px;
  text-align: center;
  color: white;
  
  @media (max-width: 640px) {
    padding: 32px 20px;
  }
`;

const BrandIcon = styled.div`
  font-size: 80px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 640px) {
    font-size: 60px;
  }
`;

const BrandLogo = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  padding: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 640px) {
    width: 100px;
    height: 100px;
  }
`;

const BrandName = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 8px 0;
  
  @media (max-width: 640px) {
    font-size: 32px;
  }
`;

const BrandDescription = styled.p`
  font-size: 18px;
  opacity: 0.95;
  margin: 0 0 24px 0;
  
  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const SocialButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
  
  @media (max-width: 640px) {
    gap: 8px;
  }
`;

const SocialButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  text-decoration: none;

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 20px;
  }
  
  @media (max-width: 640px) {
    width: 44px;
    height: 44px;
    
    svg {
      font-size: 18px;
    }
  }
`;

const InstagramButton = styled(SocialButton)`
  &:hover {
    color: #E4405F;
  }
`;

const WhatsAppButton = styled(SocialButton)`
  &:hover {
    color: #25D366;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  
  @media (max-width: 640px) {
    font-size: 20px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.9);
  
  @media (max-width: 968px) {
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    gap: 12px;
  }
`;

const MobileSearchWrapper = styled.div`
  display: none;
  
  @media (max-width: 968px) {
    display: block;
    width: 100%;
    margin-bottom: 12px;
  }
`;

const MobileFilterToggle = styled.button`
  display: none;
  
  @media (max-width: 968px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 18px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border: 2px solid rgba(226, 232, 240, 0.8);
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 12px;
    
    &:hover {
      border-color: #5efba9;
      box-shadow: 0 4px 12px rgba(94, 251, 169, 0.2);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    svg {
      font-size: 18px;
      transition: transform 0.3s;
    }
  }
`;

const MobileFilterPanel = styled.div`
  display: none;
  
  @media (max-width: 968px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 12px;
    border: 2px solid rgba(226, 232, 240, 0.8);
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.05),
      0 2px 4px -1px rgba(0, 0, 0, 0.03);
    margin-bottom: 16px;
    animation: slideDown 0.3s ease-out;
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const MobileFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;

  input {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #5efba9;
  }
  
  &:hover {
    color: #10b981;
  }
`;

const MobileFilterLabel = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
`;

const MobileCategoriesContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const MobilePriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
`;

const MobilePriceDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  border-radius: 10px;
  color: #0f172a;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
`;

const MobileClearFilters = styled.button`
  width: 100%;
  padding: 12px 18px;
  margin-top: 8px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
    color: #374151;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DesktopToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const ToolbarTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 968px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 968px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ToolbarBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  width: 100%;
  
  @media (max-width: 968px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-top: 12px;
  }
`;

const SortSelect = styled.select`
  padding: 11px 18px;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  min-width: 180px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: #5efba9;
    box-shadow: 0 2px 8px rgba(94, 251, 169, 0.15);
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.15), 0 2px 8px rgba(94, 251, 169, 0.2);
  }
  
  @media (max-width: 968px) {
    width: 100%;
    min-width: unset;
    padding: 14px 18px;
    font-size: 15px;
    min-height: 48px;
    touch-action: manipulation;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const CategoriesFilterContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  
  @media (max-width: 968px) {
    width: 100%;
  }
`;

const CategoryChip = styled.button`
  padding: 7px 16px;
  border: 2px solid ${props => props.$selected ? 'rgba(94, 251, 169, 0.6)' : 'rgba(226, 232, 240, 0.8)'};
  border-radius: 20px;
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, #5efba9 0%, #34d399 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.$selected ? '#0f172a' : '#475569'};
  font-size: 13px;
  font-weight: ${props => props.$selected ? '700' : '600'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: ${props => props.$selected 
    ? '0 2px 8px rgba(94, 251, 169, 0.3)' 
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    border-color: ${props => props.$selected ? 'rgba(94, 251, 169, 0.8)' : '#5efba9'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$selected 
      ? '0 4px 12px rgba(94, 251, 169, 0.4)' 
      : '0 3px 8px rgba(94, 251, 169, 0.2)'};
    background: ${props => props.$selected
      ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 968px) {
    padding: 10px 18px;
    font-size: 14px;
    min-height: 44px;
    touch-action: manipulation;
  }
`;

const PriceFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 968px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const PriceSliderWrapper = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  
  @media (max-width: 968px) {
    width: 100%;
    max-width: 100%;
  }
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 36px;
  background: linear-gradient(180deg, transparent 0%, rgba(226, 232, 240, 0.8) 20%, rgba(226, 232, 240, 0.8) 80%, transparent 100%);
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const PriceLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::after {
    content: ':';
    color: #9ca3af;
  }
`;

const PriceDisplay = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  min-width: 100px;
  text-align: center;
  padding: 8px 14px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  letter-spacing: 0.3px;
  
  @media (max-width: 968px) {
    width: 100%;
    min-width: unset;
  }
`;

const ProductsSection = styled.div`
  flex: 1;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const SkeletonCard = styled.div`
  border-radius: 18px;
  background: linear-gradient(90deg, #EDE5D8 25%, #F5EFE5 50%, #EDE5D8 75%);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.4s ease-in-out infinite;
  aspect-ratio: 3 / 4;
  min-height: 320px;

  @keyframes skeletonShimmer {
    0% { background-position: 200% center; }
    100% { background-position: -200% center; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: #6b7280;
`;

function Brand() {
  const { brandSlug } = useParams();
  const { products: allProducts, loading: productsLoading } = useProducts();
  const { brands: allBrands, loading: brandsLoading } = useBrands();
  const { categories: allCategories } = useCategories();
  const { language, isArabic } = useLanguage();
  
  const [sortBy, setSortBy] = useState('popular');
  const [salesData, setSalesData] = useState({});
  const [reviewsData, setReviewsData] = useState({});
  const [salesLoading, setSalesLoading] = useState(true);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [showDiscountedOnly, setShowDiscountedOnly] = useState(false);
  
  // Reset to default price range
  useEffect(() => {
    setPriceRange({ min: 0, max: 1000 });
  }, []);

  // Popularity data: paid purchases + approved reviews
  useEffect(() => {
    const load = async () => {
      if (!allProducts || allProducts.length === 0) {
        setSalesLoading(false);
        return;
      }
      const activeIds = allProducts.filter((p) => p.status === 'active').map((p) => p.id);
      if (activeIds.length === 0) {
        setSalesLoading(false);
        return;
      }
      setSalesLoading(true);
      try {
        const [sales, reviews] = await Promise.all([
          fetchSalesDataPublicSafe(activeIds),
          fetchReviewsData(activeIds),
        ]);
        setSalesData(sales);
        setReviewsData(reviews);
      } catch (e) {
        console.error('Error loading popularity data:', e);
      } finally {
        setSalesLoading(false);
      }
    };
    load();
  }, [allProducts]);

  // Find brand by slug (from database slug field) - only active brands
  const brand = allBrands
    .filter(b => b.is_active === true || b.is_active === 'true' || b.is_active === 1)
    .find(b => {
      // First try to match by database slug field
      if (b.slug && b.slug.toLowerCase() === brandSlug.toLowerCase()) {
        return true;
      }
      // Fallback: match by slugified name (for backwards compatibility)
      const nameSlug = slugify(b.name);
      return nameSlug === brandSlug;
    });

  // Parse banner color from brand data
  const getBannerGradient = () => {
    if (!brand || !brand.banner_color) {
      return 'linear-gradient(135deg, #5efba9 0%, #34D399 100%)';
    }
    
    try {
      const colorData = typeof brand.banner_color === 'string' 
        ? JSON.parse(brand.banner_color)
        : brand.banner_color;
      
      const color1 = colorData.color1 || '#5efba9';
      const color2 = colorData.color2 || '#34D399';
      
      return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
    } catch (e) {
      console.warn('Could not parse banner_color, using default gradient');
      return 'linear-gradient(135deg, #5efba9 0%, #34D399 100%)';
    }
  };

  // Get categories that have products from this brand (exactly like Categories.js logic)
  const brandCategories = useMemo(() => {
    if (!brand || !allCategories.length || !allProducts.length) return [];
    
    // Get all category IDs from active products of this brand
    const categoryIdsWithProducts = [...new Set(
      allProducts
        .filter(product => 
          product.brand_id === brand.id && 
          product.category_id && 
          product.status === 'active'
        )
        .map(product => product.category_id)
        .filter(Boolean)
    )];
    
    // Filter categories to only include those that have products from this brand
    return allCategories.filter(cat => {
      return cat.id && categoryIdsWithProducts.includes(cat.id);
    });
  }, [allCategories, allProducts, brand]);

  // Map categories to display format (exactly like Categories.js)
  const displayCategories = useMemo(() => {
    return brandCategories.map(cat => ({
      id: cat.id,
      name: language === 'ar' ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      slug: slugify(cat.name_en || cat.name),
      img: cat.img
    }));
  }, [brandCategories, language]);

  // Filter products by brand and price (memoized to avoid re-renders)
  const filteredProducts = useMemo(() => {
    let products = [];
    
    if (brand) {
      // Filter by brand and active status
      products = allProducts.filter(p => 
        p.brand_id === brand.id && p.status === 'active'
      );
    }

    // Apply category filter (exactly like Categories.js)
    if (selectedCategory !== 'all') {
      products = products.filter(p => {
        return p.category_id === selectedCategory && p.status === 'active';
      });
    }

    // Apply price range filter (exactly like Categories.js)
    products = products.filter(p => {
      const price = parseFloat(p.price) || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Apply discount filter
    if (showDiscountedOnly) {
      products = products.filter(p => {
        const discount = p.dis && p.dis !== 'no' ? parseFloat(p.dis) : null;
        return discount !== null && discount > 0;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      products = products.filter(p => {
        const productName = (p.name || '').toLowerCase();
        const productNameAr = (p.name_ar || '').toLowerCase();
        
        return productName.includes(query) || productNameAr.includes(query);
      });
    }

    return products;
  }, [allProducts, brand, selectedCategory, priceRange, searchQuery, showDiscountedOnly]);

  // Copy store link to clipboard

  // Get Instagram URL
  const getInstagramUrl = () => {
    if (!brand?.insta) return null;
    
    let instaUrl = brand.insta.trim();
    
    // If it's already a full URL, use it as is
    if (instaUrl.startsWith('http://') || instaUrl.startsWith('https://')) {
      return instaUrl;
    }
    
    // Remove @ and any instagram.com prefix, then construct URL
    const username = instaUrl.replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//, '').trim();
    return `https://instagram.com/${username}`;
  };

  // Get WhatsApp URL
  const getWhatsAppUrl = () => {
    if (!brand?.phone) return null;
    
    // Format phone number for WhatsApp (remove all non-digit characters except +)
    let phone = brand.phone.trim();
    
    // Remove spaces, dashes, parentheses, etc.
    phone = phone.replace(/[\s\-\(\)]/g, '');
    
    // If it doesn't start with +, assume it's a local number and add country code
    // For Oman, country code is +968
    if (!phone.startsWith('+')) {
      // Remove leading zeros
      phone = phone.replace(/^0+/, '');
      // Add Oman country code if not present
      if (!phone.startsWith('968')) {
        phone = '968' + phone;
      }
      phone = '+' + phone;
    }
    
    // WhatsApp URL format: https://wa.me/PHONENUMBER (with country code, no +)
    const whatsappNumber = phone.replace(/^\+/, '');
    return `https://wa.me/${whatsappNumber}`;
  };

  // Sort products — return null while waiting for sales data on popular sort to avoid flash
  const sortedProducts = useMemo(() => {
    if (sortBy === 'popular' && salesLoading) return null;
    const products = [...filteredProducts];
    switch (sortBy) {
      case 'price-low':
        return [...products].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case 'price-high':
        return [...products].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      case 'newest':
        return [...products].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'popular':
      default:
        return sortProductsByBoostAndPopularity(products, salesData, reviewsData);
    }
  }, [filteredProducts, sortBy, salesData, reviewsData, salesLoading]);

  // Always keep boosted products first, regardless of selected sort.
  const boostedFirstProducts = useMemo(() => {
    if (!sortedProducts) return null;
    return [...sortedProducts].sort((a, b) => {
      if (a.boost && !b.boost) return -1;
      if (!a.boost && b.boost) return 1;
      return 0;
    });
  }, [sortedProducts]);

  // Format products for ProductCard — null while sales data is still loading
  const displayProducts = boostedFirstProducts === null ? null : boostedFirstProducts.map(product => {
    const discount = product.dis && product.dis !== 'no' ? parseFloat(product.dis) : null;
    const discountedPrice = discount && product.price ? (product.price * (1 - discount / 100)).toFixed(2) : null;
    
    return {
      id: product.id,
      title: product.name,
      name: product.name,
      name_ar: product.name_ar,
      price: discount ? discountedPrice : product.price,
      originalPrice: discount ? product.price : null,
      discount,
      currency: product.currency,
      image: product.img || product.images?.[0] || 'https://via.placeholder.com/300x240',
      brand: brand?.name,
      seller: { id: product.brand_id || 1, name: brand?.name || 'Seller' }
    };
  });

  if (productsLoading || brandsLoading) {
    return (
      <Container>
        <Header>
          <Title>Loading...</Title>
        </Header>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading...
        </div>
      </Container>
    );
  }

  if (!brand) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateIcon>🏷️</EmptyStateIcon>
          <EmptyStateTitle>Brand Not Found</EmptyStateTitle>
          <EmptyStateText>The brand you're looking for doesn't exist.</EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <BrandHeader $gradient={getBannerGradient()}>
        {brand.img && brand.img.trim() !== '' ? (
          <BrandLogo src={brand.img} alt={brand.name || 'Brand'} onError={(e) => {
            // Fallback to emoji if image fails to load
            e.target.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.style.cssText = 'font-size: 80px; margin-bottom: 16px; display: flex; align-items: center; justify-content: center;';
            fallback.textContent = '🏷️';
            e.target.parentElement.appendChild(fallback);
          }} />
        ) : (
          <BrandIcon>🏷️</BrandIcon>
        )}
        <BrandName>{brand.name}</BrandName>
        <BrandDescription>
          {displayProducts === null
            ? (isArabic ? 'جاري التحميل...' : 'Loading...')
            : isArabic 
              ? `${displayProducts.length} ${displayProducts.length === 1 ? 'منتج متاح' : 'منتجات متاحة'}`
              : `${displayProducts.length} ${displayProducts.length === 1 ? 'Product' : 'Products'} Available`
          }
        </BrandDescription>
        <SocialButtonsContainer>
          {getInstagramUrl() && (
            <InstagramButton
              href={getInstagramUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Instagram"
            >
              <FaInstagram />
            </InstagramButton>
          )}
          {getWhatsAppUrl() && (
            <WhatsAppButton
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact on WhatsApp"
            >
              <FaWhatsapp />
            </WhatsAppButton>
          )}
        </SocialButtonsContainer>
      </BrandHeader>

      <Header>
        <TitleRow>
          <Title>{isArabic ? 'جميع المنتجات' : 'All Products'}</Title>
        </TitleRow>
        
        {/* Mobile Search Bar - Always Visible */}
        <MobileSearchWrapper>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </MobileSearchWrapper>
        
        {/* Mobile Filter Toggle */}
        <MobileFilterToggle onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiFilter />
            {isArabic ? 'الفلاتر' : 'Filters'}
            {(selectedCategory !== 'all' || priceRange.min > 0 || priceRange.max < 1000 || showDiscountedOnly) && (
              <span style={{ 
                background: '#5efba9', 
                color: '#0f172a', 
                borderRadius: '12px', 
                padding: '2px 8px', 
                fontSize: '12px', 
                fontWeight: '700' 
              }}>
                {(selectedCategory !== 'all' ? 1 : 0) + (priceRange.min > 0 || priceRange.max < 1000 ? 1 : 0) + (showDiscountedOnly ? 1 : 0)}
              </span>
            )}
          </span>
          {isMobileFilterOpen ? <FiX /> : <FiChevronRight style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />}
        </MobileFilterToggle>

        {/* Mobile Filter Panel */}
        <MobileFilterPanel $isOpen={isMobileFilterOpen}>
          {/* Categories Filter */}
          {displayCategories.length > 0 && (
            <MobileFilterSection>
              <MobileFilterLabel>{isArabic ? 'الفئات' : 'Categories'}</MobileFilterLabel>
              <MobileCategoriesContainer>
                <CategoryChip
                  $selected={selectedCategory === 'all'}
                  onClick={() => setSelectedCategory('all')}
                >
                  {language === 'ar' ? 'الكل' : 'All'}
                </CategoryChip>
                {displayCategories.map(category => (
                  <CategoryChip
                    key={category.id}
                    $selected={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                  </CategoryChip>
                ))}
              </MobileCategoriesContainer>
            </MobileFilterSection>
          )}

          {/* Price Range Filter */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'نطاق السعر' : 'Price Range'}</MobileFilterLabel>
            <MobilePriceContainer>
              <PriceRangeSlider
                min={0}
                max={1000}
                value={priceRange}
                onChange={setPriceRange}
                inline={true}
              />
              <MobilePriceDisplay>
                {priceRange.min} - {priceRange.max} OMR
              </MobilePriceDisplay>
            </MobilePriceContainer>
          </MobileFilterSection>

          {/* Discounted Items Filter */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'العروض والخصومات' : 'Deals & Discounts'}</MobileFilterLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={showDiscountedOnly}
                onChange={(e) => setShowDiscountedOnly(e.target.checked)}
              />
              <span>{isArabic ? 'منتجات مخفضة' : 'Discounted Items'}</span>
            </CheckboxLabel>
          </MobileFilterSection>

          {/* Sort */}
          <MobileFilterSection>
            <MobileFilterLabel>{isArabic ? 'ترتيب حسب' : 'Sort By'}</MobileFilterLabel>
            <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="popular">{isArabic ? 'الأكثر مبيعاً' : 'Best Seller'}</option>
              <option value="price-low">{isArabic ? 'السعر: من الأقل للأعلى' : 'Price: Low to High'}</option>
              <option value="price-high">{isArabic ? 'السعر: من الأعلى للأقل' : 'Price: High to Low'}</option>
              <option value="newest">{isArabic ? 'الأحدث' : 'Newest'}</option>
            </SortSelect>
          </MobileFilterSection>

          {/* Clear Filters Button */}
          {(selectedCategory !== 'all' || priceRange.min > 0 || priceRange.max < 1000 || sortBy !== 'popular' || showDiscountedOnly) && (
            <MobileClearFilters onClick={() => {
              setSelectedCategory('all');
              setPriceRange({ min: 0, max: 1000 });
              setSortBy('popular');
              setShowDiscountedOnly(false);
            }}>
              {isArabic ? 'مسح الفلاتر' : 'Clear Filters'}
            </MobileClearFilters>
          )}
        </MobileFilterPanel>

        {/* Desktop Toolbar */}
        <DesktopToolbar>
          <Toolbar>
            <ToolbarTopRow>
              <ToolbarSection style={{ flex: '1 1 300px', minWidth: '250px' }}>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
              </ToolbarSection>
              
              {displayCategories.length > 0 && (
                <>
                  <FilterDivider />
                  <ToolbarSection style={{ flex: '1 1 300px', minWidth: '200px' }}>
                    <CategoriesFilterContainer>
                      <CategoryChip
                        $selected={selectedCategory === 'all'}
                        onClick={() => setSelectedCategory('all')}
                      >
                        {language === 'ar' ? 'الكل' : 'All'}
                      </CategoryChip>
                      {displayCategories.map(category => (
                        <CategoryChip
                          key={category.id}
                          $selected={selectedCategory === category.id}
                          onClick={() => setSelectedCategory(category.id)}
                        >
                          {category.name}
                        </CategoryChip>
                      ))}
                    </CategoriesFilterContainer>
                  </ToolbarSection>
                </>
              )}
              
              <FilterDivider />
              
              <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="popular">{isArabic ? 'الأكثر مبيعاً' : 'Best Seller'}</option>
                <option value="price-low">{isArabic ? 'السعر: من الأقل للأعلى' : 'Price: Low to High'}</option>
                <option value="price-high">{isArabic ? 'السعر: من الأعلى للأقل' : 'Price: High to Low'}</option>
                <option value="newest">{isArabic ? 'الأحدث' : 'Newest'}</option>
              </SortSelect>
            </ToolbarTopRow>
            
            <ToolbarBottomRow>
              <PriceFilterContainer>
                <PriceLabel>{isArabic ? 'السعر' : 'Price'}</PriceLabel>
                <PriceSliderWrapper>
                  <PriceRangeSlider
                    min={0}
                    max={1000}
                    value={priceRange}
                    onChange={setPriceRange}
                    inline={true}
                  />
                </PriceSliderWrapper>
                <PriceDisplay>
                  {priceRange.min} - {priceRange.max} OMR
                </PriceDisplay>
              </PriceFilterContainer>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', borderLeft: '1px solid rgba(226, 232, 240, 0.6)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={showDiscountedOnly}
                    onChange={(e) => setShowDiscountedOnly(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#5efba9', cursor: 'pointer' }}
                  />
                  <span>{isArabic ? 'منتجات مخفضة' : 'Discounted Items'}</span>
                </label>
              </div>
            </ToolbarBottomRow>
          </Toolbar>
        </DesktopToolbar>
      </Header>

      <Content>
        <ProductsSection>
          {displayProducts === null ? (
            <ProductsGrid>
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </ProductsGrid>
          ) : displayProducts.length > 0 ? (
            <ProductsGrid>
              {displayProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </ProductsGrid>
          ) : (
            <EmptyState>
              <EmptyStateIcon>📦</EmptyStateIcon>
              <EmptyStateTitle>{isArabic ? 'لم يتم العثور على منتجات' : 'No Products Found'}</EmptyStateTitle>
              <EmptyStateText>
                {isArabic 
                  ? 'هذه العلامة التجارية لا تحتوي على أي منتجات متاحة في الوقت الحالي.'
                  : 'This brand doesn\'t have any products available at the moment.'
                }
              </EmptyStateText>
            </EmptyState>
          )}
        </ProductsSection>
      </Content>
    </Container>
  );
}

export default Brand;

